import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Checkbox } from "@bookdental/ui";
import { ROTULO_DO_CAMPO } from "@bookdental/schemas";
import { cn } from "@/lib/cn";

type Campo = { chave: string; ligado: boolean };

/** Campos da etiqueta: arrastar muda a ordem em que aparecem, a caixa liga e desliga. */
export function ListaDeCampos({ campos, onChange }: { campos: Campo[]; onChange: (campos: Campo[]) => void }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const soltar = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const de = campos.findIndex((c) => c.chave === e.active.id);
    const para = campos.findIndex((c) => c.chave === e.over!.id);
    onChange(arrayMove(campos, de, para));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={soltar}>
      <SortableContext items={campos.map((c) => c.chave)} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
          {campos.map((c) => (
            <Linha
              key={c.chave}
              campo={c}
              onAlternar={(ligado) => onChange(campos.map((x) => (x.chave === c.chave ? { ...x, ligado } : x)))}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function Linha({ campo, onAlternar }: { campo: Campo; onAlternar: (ligado: boolean) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: campo.chave });
  const rotulo = ROTULO_DO_CAMPO[campo.chave as keyof typeof ROTULO_DO_CAMPO] ?? campo.chave;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("flex items-center gap-2 bg-white px-2 py-1.5 dark:bg-card", isDragging && "relative z-10 shadow-md")}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Mover ${rotulo}`}
        className="flex h-7 w-6 shrink-0 cursor-grab touch-none items-center justify-center text-zinc-400 hover:text-zinc-700 active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox checked={campo.ligado} onCheckedChange={(v) => onAlternar(v === true)} aria-label={`Mostrar ${rotulo}`} />
      <span className={cn("text-sm", campo.ligado ? "text-zinc-900 dark:text-foreground" : "text-zinc-400 dark:text-muted-foreground")}>{rotulo}</span>
    </li>
  );
}
